import { useFormContext } from "react-hook-form";
import useSetFormData from "repoV2/features/Common/modules/FormData/utils/useSetFormData";
import { FORM_KEYS } from "repoV2/features/Common/modules/FormData/FormData.constants";
import useGetFormData from "repoV2/features/Common/modules/FormData/utils/useGetFormData";
import { IExlySearchableDropdownOption } from "repoV2/features/Common/modules/Form/modules/ExlySearchableDropdown/ExlySearchableDropdown.interfaces";
import { IUserDetails } from "repoV2/features/Common/modules/FormData/interfaces/UserDetailsFormGroup.interfaces";
import { debounce } from "repoV2/features/Common/utils/debounce";
import { checkIsStringEmpty } from "repoV2/utils/common/dataTypes/string";
import {
    checkout_form,
    checkout_form_error_strings,
} from "../../../../../CheckoutForm/CheckoutForm.constants";
import { useUserDetailsFormContext } from "../UserDetailsFormContextProvider";
import { getUserFormFieldName } from "../../UserDetailsFormGroup.utils";
import useCheckShiprocketPinCode from "../../../MerchandiseDetailsFormGroup/utils/useCheckShiprocketPinCode";

const {
    international_details: { field_names },
} = checkout_form;

const { required } = checkout_form_error_strings;

export const useInternationalDetailsFormGroup = ({
    currencyName,
    showInternationalInputFields,
}: {
    showInternationalInputFields: boolean;
    currencyName: string;
}) => {
    const { getValues } = useFormContext();
    const { setFormData } = useSetFormData();
    const { userId } = useUserDetailsFormContext();

    const userDetails =
        useGetFormData<{ [userId: string]: IUserDetails }>({
            key: `${FORM_KEYS.user_details}`,
        }) || {};
    const { checkShiprocketPinCode } = useCheckShiprocketPinCode();

    const debouncedCheckShiprocketPinCode = debounce(
        checkShiprocketPinCode,
        500
    );

    // Payment Method selected by the customer, incase of multiple customers only
    // the first customer has the option to select this value
    const isSingleUser = Object.keys(userDetails).length === 1;

    const setShiprocketPinCodeFormData = ({
        fieldName,
    }: {
        fieldName: string;
    }) => {
        const data = getValues();
        setFormData(
            `${FORM_KEYS.user_details}.${userId}.${FORM_KEYS.international_details}.${fieldName}`,
            data[getUserFormFieldName(userId, fieldName)]
        );

        if (data[getUserFormFieldName(userId, fieldName)]) {
            debouncedCheckShiprocketPinCode({
                currencyName,
                isInternationalEnabled: showInternationalInputFields,
            });
        }
    };

    const setInternationalDetailsFormData = ({
        fieldName,
    }: {
        fieldName: string;
    }) => {
        const data = getValues();
        // TODO @shivansh: setFormData should not have `value ||` in it since the dropdown has now ben fixed
        // if some other component is using this for something other than a dropdown, will need to see how to fix it.
        setFormData(
            `${FORM_KEYS.user_details}.${userId}.${FORM_KEYS.international_details}.${fieldName}`,
            data[getUserFormFieldName(userId, fieldName)]
        );
    };

    const setInternationalPaymentDetailsFormData = () => {
        const data = getValues();
        setFormData(
            `${FORM_KEYS.payment_details}.${FORM_KEYS.payment_method}`,
            data?.[field_names.payment_method]
        );
    };

    const merchandiseStateValidator = () => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(
            userId,
            field_names.user_state
        );
        const billingState: IExlySearchableDropdownOption =
            data[fieldNameWithId] || "";

        if (checkIsStringEmpty(billingState?.value)) return required;
        else return true;
    };

    const setMerchandiseStateFromData = ({
        fieldName,
        value,
    }: {
        fieldName: string;
        value?: IExlySearchableDropdownOption;
    }) => {
        const data = getValues();
        const fieldNameWithId = getUserFormFieldName(userId, fieldName);

        setFormData(
            `${FORM_KEYS.user_details}.${userId}.${fieldName}`,
            value || data[fieldNameWithId]
        );
    };

    return {
        setInternationalDetailsFormData,
        isSingleUser,
        merchandiseStateValidator,
        setShiprocketPinCodeFormData,
        setMerchandiseStateFromData,
        setInternationalPaymentDetailsFormData,
    };
};

export const validators = {
    [field_names.street_address]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value) ? required : true;
    },
    [field_names.town_city]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value) ? required : true;
    },
    [field_names.user_state]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value) ? required : true;
    },
    [field_names.postal_code]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value) ? required : true;
    },
    [field_names.payment_method]: (value: string) => {
        const _value = value.trim();
        return checkIsStringEmpty(_value) ? required : true;
    },
};
